import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Auto-open drawer when an item is added to cart
   */
  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Public open() helper to show drawer and trigger preview lazy load
   */
  open() {
    this.showDialog();

    // Ensure payment installments CTA closes drawer to avoid overlapping dialogs
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document
        .querySelector('shopify-payment-terms')
        ?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Lazy-load any Cloudinary design preview thumbnails
    this.#lazyLoadDesignPreviews();
  }

  /**
   * Public close() helper
   */
  close() {
    this.closeDialog();
  }

  /**
   * IntersectionObserver-based lazy loader for design preview images.
   * Triggers each time the drawer opens.
   */
  #lazyLoadDesignPreviews() {
    const lazyPreviews = this.querySelectorAll('img.lazy-preview[data-src]');
    if (!lazyPreviews.length) return;

    // Fallback for browsers without IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      lazyPreviews.forEach((img) => {
        img.src = img.dataset.src;
        img.style.opacity = '1';
      });
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.onload = () => (img.style.opacity = '1');
          obs.unobserve(img);
        }
      });
    }, {
      root: this, // observe relative to drawer
      rootMargin: '50px',
      threshold: 0.1,
    });

    lazyPreviews.forEach((img) => observer.observe(img));
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
